// @ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { usePermissions } from "../../hooks";
import { useTranslate } from "@refinedev/core";
import { apiVersion, apiJsonDedup } from "../../utils/api";
import { trackViewInvoiceList, trackViewInvoice, trackInvoiceDownload } from "../../utils/analytics-events";

export type Invoice = {
  id: string;
  number?: string;
  amount?: number;
  currency?: string;
  status?: string;
  invoice_pdf?: string;
  created?: number;
  property?: {
    id: string | null;
    name?: string | null;
    property_id?: string | null;
  } | null;
  profile?: {
    id?: string;
    name?: string;
    email?: string;
  } | null;
};

type CursorPaginatedResponse<T> = {
  data: T[];
  pagination: {
    limit: number;
    nextCursor?: string;
    hasMore: boolean;
  };
};

const formatAmount = (amount: number | undefined, currency: string | undefined) => {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "-";
  const divisor = 100; // Stripe-style smallest currency unit
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
    }).format(amount / divisor);
  } catch {
    return `${(amount / divisor).toFixed(2)} ${currency || ""}`.trim();
  }
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatDateTime = (value?: number) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const statusColor = (status?: string) => {
  if (!status) return "default" as const;
  const normalized = status.toLowerCase();
  if (["paid", "succeeded", "fulfilled"].includes(normalized)) return "success" as const;
  if (["draft", "open", "uncollectible", "processing"].includes(normalized)) return "warning" as const;
  if (["void", "canceled"].includes(normalized)) return "error" as const;
  return "default" as const;
};

const surfaceSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  boxShadow: (theme: any) => theme.shadows[1],
};

export const BillingInvoicesPage: React.FC = () => {
  const { hasFullAccess } = usePermissions();
  const t = useTranslate();
  const [items, setItems] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(10);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const currentPage = cursorStack.length;

  const fetchData = useCallback(async (cursor: string | undefined, pageLimit: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(pageLimit));
      if (cursor) params.set('cursor', cursor);
      
      const response = await apiJsonDedup<CursorPaginatedResponse<Invoice>>(
        `admin/${apiVersion}/billing/invoices?${params.toString()}`
      );
      
      setItems(response.data || []);
      setNextCursor(response.pagination?.nextCursor);
      setHasMore(response.pagination?.hasMore || false);
      
      // Track invoice list view
      const totalValue = response.data?.reduce((sum, inv) => sum + (inv.amount || 0), 0) || 0;
      trackViewInvoiceList(response.data?.length, totalValue, 'USD');
    } catch (e: any) {
      setError(e?.message ?? "Failed to load invoices");
      setItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const currentCursor = cursorStack[cursorStack.length - 1];
    void fetchData(currentCursor, limit);
  }, [cursorStack, limit, fetchData]);

  const handleNextPage = () => {
    if (hasMore && nextCursor) {
      setCursorStack(prev => [...prev, nextCursor]);
    }
  };

  const handlePrevPage = () => {
    if (cursorStack.length > 1) {
      setCursorStack(prev => prev.slice(0, -1));
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCursorStack([undefined]);
  };

  const showProfileColumn = hasFullAccess;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", p: 2 }}>
      <Box
        sx={{
          ...surfaceSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReceiptLongIcon fontSize="medium" /> {t("billing.invoices.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("billing.invoices.subtitle")}
          </Typography>
        </Box>
      </Box>

      <Card sx={surfaceSx}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography color="text.secondary">No invoices found</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("billing.invoices.table.invoiceNumber")}</TableCell>
                    <TableCell>{t("billing.invoices.table.issued")}</TableCell>
                    <TableCell>{t("billing.invoices.table.amount")}</TableCell>
                    <TableCell>{t("billing.invoices.table.status")}</TableCell>
                    <TableCell>{t("billing.invoices.table.property")}</TableCell>
                    {showProfileColumn && (
                      <>
                        <TableCell>{t("billing.invoices.table.profileName")}</TableCell>
                        <TableCell>{t("billing.invoices.table.profileEmail")}</TableCell>
                      </>
                    )}
                    <TableCell>{t("billing.invoices.table.invoice")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      hover
                      sx={{ "&:last-of-type td": { borderBottom: 0 }, "& td": { py: 2 }, cursor: 'pointer' }}
                      onClick={() => {
                        // Track invoice view
                        trackViewInvoice(invoice.id, invoice.amount || 0, invoice.currency || 'USD', invoice.status, undefined, invoice.property?.property_id);
                      }}
                    >
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                        {invoice.number || "-"}
                      </TableCell>
                      <TableCell>{formatDateTime(invoice.created)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{formatAmount(invoice.amount, invoice.currency)}</TableCell>
                      <TableCell>
                        {invoice.status ? (
                          <Chip
                            size="small"
                            label={invoice.status}
                            color={statusColor(invoice.status)}
                            variant={statusColor(invoice.status) === "default" ? "outlined" : "filled"}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {invoice.property?.property_id ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                            <Typography variant="body2" fontWeight={600}>
                              <a href={`/properties/${invoice.property.property_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                {invoice.property.name || "-"}
                              </a>
                            </Typography>
                          </Box>
                        ) : (
                          invoice.property?.name || "-"
                        )}
                      </TableCell>
                      {showProfileColumn && (
                        <>
                          <TableCell>
                            {invoice.profile?.name || "-"}
                          </TableCell>
                          <TableCell>
                            {invoice.profile?.email || "-"}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        {invoice.invoice_pdf ? (
                          <MuiLink
                            href={invoice.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, textDecoration: 'none' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              trackInvoiceDownload(invoice.id, undefined, undefined, invoice.property?.property_id);
                            }}
                          >
                            <PictureAsPdfIcon fontSize="small" /> PDF
                          </MuiLink>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Page {currentPage}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Size</InputLabel>
            <Select
              value={limit}
              label="Size"
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              sx={{ '& .MuiSelect-select': { textAlign: 'center', paddingRight: '24px' } }}
            >
              {[5, 10, 15, 20].map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ justifyContent: 'center' }}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              startIcon={<NavigateBeforeIcon />}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleNextPage}
              disabled={!hasMore}
              endIcon={<NavigateNextIcon />}
            >
              Next
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default BillingInvoicesPage;
